import { applyStateTransition, transitionState } from "../../../utils/GeneralHelpers";
import {
  CreateTrainer,
  SearchTrainer,
  DeleteTrainer,
  UpdateTrainerName,
  UpdateTrainerDescription,
  UpdateTrainerExperienceInYears,
  UpdateTrainerThumbnail,
  CreateTrainerInput,
  Trainer,
  UpdateTrainerNameInput,
  UpdateTrainerDescriptionInput,
  UpdateTrainerExperienceInYearsInput,
  UpdateTrainerThumbnailInput,
  GetTrainer,
  UpdateTrainerState,
  UpdateTrainerSignature
} from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Trainers/PathItems";
import { generateUUID, Result, TransportAbstractionBuilder, TransportContext } from "@peercolab/engine";

export function mockTrainerInterceptors(builder: TransportAbstractionBuilder) {
  const dbTrainers: Trainer[] = [];
  const dbTrainersPublished: Trainer[] = [];

  const getTrainer = (ctx: string | TransportContext): Result<Trainer> => {
    const id = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('trainerId');
    const trainer = dbTrainers.find(t => t.id === id);
    if (!trainer) {
      return Result.notFound('Backoffice.Trainer.NotFound', `Trainer with id ${id} not found`);
    }
    return Result.ok(trainer);
  };

  const saveTrainer = (result: Result<Trainer>, ctx: TransportContext): Result<Trainer> => {
    if (!result.success) {
      return result;
    }
    const trainer = result.value;
    const existingIndex = dbTrainers.findIndex(t => t.id === trainer.id);
    if (existingIndex !== -1) {
      const existingPublishedIndex = dbTrainersPublished.findIndex(t => t.id === trainer.id);
      dbTrainers[existingIndex] = applyStateTransition(dbTrainersPublished, existingPublishedIndex, result.meta, trainer, trainer.state, (itm, newState) => {
        itm.state = newState;
        return itm;
      });
    } else {
      dbTrainers.push(trainer);
    }
    return Result.ok(trainer);
  };

  return builder
    .intercept(new SearchTrainer().handle(async (input, ctx) => {
      const filtered = dbTrainers
        .slice(input.offset, input.offset + input.limit)
        .filter(t =>
          t.name.toLowerCase().includes(input.query.toLowerCase()) ||
          t.description.toLowerCase().includes(input.query.toLowerCase())
        );
      return Result.ok(filtered);
    }))
    .intercept(new GetTrainer().handle(async (input, ctx) => {
        return getTrainer(ctx);
    }))
    .intercept(new CreateTrainer().handle(async (input: CreateTrainerInput, ctx) => {
      const newTrainer: Trainer = {
        id: input.trainerId ?? generateUUID(),
        name: input.name,
        description: input.description ?? '',
        thumbnail: undefined,
        experienceInYears: input.experienceInYears ?? 0,
        state: { state: 'draft', isDirty: false }
      };
      return saveTrainer(Result.ok(newTrainer), ctx);
    }))
    .intercept(new DeleteTrainer().handle(async (input, ctx) => {
      const id = ctx.getPathParameter<string>('trainerId');
      const idx = dbTrainers.findIndex(t => t.id === id);
      if (idx === -1) {
        return Result.notFound('Backoffice.Trainer.NotFound', `Trainer with id ${id} not found`);
      }
      dbTrainers.splice(idx, 1);
      return Result.ok(undefined);
    }))
    .intercept(new UpdateTrainerName().handle(async (input: UpdateTrainerNameInput, ctx) => {
      return saveTrainer(getTrainer(ctx)
        .maybe((value, meta) => {
          value.name = input.name;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateTrainerDescription().handle(async (input: UpdateTrainerDescriptionInput, ctx) => {
      return saveTrainer(getTrainer(ctx)
        .maybe((value, meta) => {
          value.description = input.description;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateTrainerExperienceInYears().handle(async (input: UpdateTrainerExperienceInYearsInput, ctx) => {
      return saveTrainer(getTrainer(ctx)
        .maybe((value, meta) => {
          value.experienceInYears = input.experienceInYears;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateTrainerThumbnail().handle(async (input: UpdateTrainerThumbnailInput, ctx) => {
      return saveTrainer(getTrainer(ctx)
        .maybe((value, meta) => {
          if (!input.image) {
            value.thumbnail = undefined;
          } else {
            value.thumbnail = { url: 'data:' + input.image.mimeType + ';base64,' + input.image.base64Content };
          }
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateTrainerSignature().handle(async (input, ctx) => {
      return saveTrainer(getTrainer(ctx)
        .maybe((value, meta) => {
          if (!input.image) {
            value.signature = undefined;
          } else {
            value.signature = { url: 'data:' + input.image.mimeType + ';base64,' + input.image.base64Content };
          }
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateTrainerState().handle(async (input, ctx) => {
      return saveTrainer(getTrainer(ctx)
        .maybe((value, meta) => {
          value.state = transitionState(meta, value.state, input.stateTransition);
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }));
}
